/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { EVACDeviceFormData } from "../../components/devices/lct/types/providerTypes"
import {
    TTRDeviceFormData,
    TypeTestFormData,
} from "../../components/devices/ttr/types/providerTypes"
import { typeTestStandards } from "../../constants/ttr/registerDevice"
import { AppError } from "../../errors/errorProcessors"
import { TEVACDeviceResponseType } from "../../services/middleware/types/devices/lct/publishEvacDeviceTypes"
import {
    TTTRDeviceResponseType,
    TTypeTestPostBody,
    TTypeTestPostBodyStandardName,
} from "../../services/middleware/types/devices/ttr/publishDeviceTypes"

/** Creates the react hook form register name for the "Fully Tested" switch for the provided standard eg. G99A -> G99AFullyTested */
export const getFullyTestedRegisterName = (standardName: string): string =>
    `${standardName}FullyTested`

/** Creates the react hook form register name for the "Partially Tested" switch for the provided standard eg. G99A -> G99APartiallyTested */
export const getPartiallyTestedRegisterName = (standardName: string): string =>
    `${standardName}PartiallyTested`

/** Creates the react hook form display name for the "Fully Tested" switch for the provided standard eg. G99A -> G99A Fully Tested */
export const getFullyTestedLabelName = (standardName: string): string =>
    `${standardName} Fully Tested`

/** Creates the react hook form display name for the "Partially Tested" switch for the provided standard eg. G99A -> G99A Partially Tested */
export const getPartiallyTestedLabelName = (standardName: string): string =>
    `${standardName} Partially Tested`

/** Creates the publish device endpoint POST body name for the "Fully Tested" argument for the provided standard eg. G99A -> FTT_G99A */
export const getFullyTestedPostBodyName = (
    standardName: string
): TTypeTestPostBodyStandardName =>
    `FTT_${standardName}` as TTypeTestPostBodyStandardName

/** Creates the publish device endpoint POST body name for the "Partially Tested" argument for the provided standard eg. G99A -> PTT_G99A */
export const getPartiallyTestedPostBodyName = (
    standardName: string
): TTypeTestPostBodyStandardName =>
    `PTT_${standardName}` as TTypeTestPostBodyStandardName

/** Creates the react hook form attachment description register name for the provided filename */
export const getAttachmentDescriptionRegisterName = (
    fileName: string
): string => `${fileName.split(".")[0]}MetaDescription`

/** Creates the react hook form attachment restricted switch register name for the provided filename */
export const getAttachmentRestrictedRegisterName = (fileName: string): string =>
    `${fileName.split(".")[0]}MetaRestricted`

/**
 * Returns TTR Device form data where all fields are empty.
 *
 * Note: Avoid using `undefined` when providing a default value
 */
export const getEmptyTTRDeviceFormData = (): TTRDeviceFormData => ({
    typeTestDetails: typeTestStandards.map((standard) => ({
        name: standard.name,
        displayName: standard.displayName,
        fullyTested: false,
        partiallyTested: false,
    })),
    coreDetails: {
        manufacturer: null,
        model: "",
        deviceCategory: null,
        deviceType: null,
        registeredCapacity: "",
        numberOfPhases: null,
        softwareVersion: "",
        hardwareVersion: "",
        additionalComments: "",
    },
    stagedAttachments: [],
})

/**
 * Returns EVAC Device form data where all fields are empty.
 *
 * Note: Avoid using `undefined` when providing a default value
 */
export const getEmptyEVACDeviceFormData = (): EVACDeviceFormData => ({
    manufacturerId: null,
    deviceMake: "",
    deviceModel: "",
    deviceModelRef: "",
    barcodeNumber: "",
    phases: "",
    voltage: "",
    maxCurrentAmpPerPhase: "",
    acOnlyOutput: null,
    v2gCapability: null,
    v2gExportCapacity: "",
    v2gCompletionDate: "",
    v2gG99gbG999niBoth: null,
    v2gTtrStatus: "",
    comments: "",
    stagedAttachments: [],
    systemReference: null,
})

export const mapEVACDeviceResponseToDeviceFormData = <
    T extends TEVACDeviceResponseType,
>(
    output?: T
): EVACDeviceFormData => {
    if (!output) {
        return getEmptyEVACDeviceFormData()
    }

    return {
        manufacturerId: output.ManufacturerID,
        deviceMake: output.Make ?? "",
        deviceModel: output.Model ?? "",
        deviceModelRef: output.ModelReference ?? "",
        barcodeNumber: output.BarcodeNumber ?? "",
        phases: output.Phases,
        voltage: output.Voltage,
        maxCurrentAmpPerPhase: output.MaxCurrentAmpPerPhase,
        acOnlyOutput: ["Yes", "No"].includes(output.ACOnlyOutput as never)
            ? output.ACOnlyOutput === "Yes"
            : null,
        v2gCapability: ["Yes", "No"].includes(output.V2G_Capability as never)
            ? output.V2G_Capability === "Yes"
            : null,
        v2gExportCapacity: output.V2G_ExportCapacity,
        v2gCompletionDate: output.V2G_CompletionDate ?? "",
        v2gG99gbG999niBoth: output.V2G_G99GB_G99NI_Both,
        v2gTtrStatus: output.V2G_TTRStatus ?? "",
        comments: output.Comments ?? "",
        stagedAttachments: [],
        systemReference: output.SystemReference ?? "",
    }
}

/** Maps the device responses from API endpoints to Device form data */
export const mapDeviceResponseToDeviceFormData = <
    T extends TTTRDeviceResponseType,
>(
    output?: T
): TTRDeviceFormData => {
    if (!output) {
        return getEmptyTTRDeviceFormData()
    }

    const typeTestDetails: TypeTestFormData[] = typeTestStandards.map(
        (standard) => {
            const fullyTestedName = getFullyTestedPostBodyName(standard.name)
            const partiallyTestedName = getPartiallyTestedPostBodyName(
                standard.name
            )

            return {
                name: standard.name,
                displayName: standard.displayName,
                partiallyTested: output[partiallyTestedName] === "Y",
                fullyTested: output[fullyTestedName] === "Y",
            }
        }
    )

    return {
        coreDetails: {
            manufacturer: output.ManufacturerID ?? null,
            model: output.ModelNo ?? "",
            deviceType: output.DeviceTypeID ?? null,
            deviceCategory: output.DeviceCategoryID ?? null,
            registeredCapacity: output.RegisteredCapacity ?? null,
            numberOfPhases: output.PhaseID ?? null,
            softwareVersion: output.SoftwareVersion ?? "",
            hardwareVersion: output.HardwareVersion ?? "",
            additionalComments: output.DeviceComments ?? "",
        },
        typeTestDetails,
        stagedAttachments: [],
    }
}

/**
 * Helper for checking if given errors already exist in a list of errors.
 * Calls `onNewError` for each error that does not
 */
export const checkContextErrors = (
    errorsToCheck: (AppError | undefined)[],
    currentErrors: AppError[],
    onNewError: (error: AppError) => void
): void => {
    errorsToCheck.forEach((error) => {
        if (error && !currentErrors.includes(error)) {
            onNewError(error)
        }
    })
}

/**
 * Returns the type test section of a POST body used for making requests to the publish/amend endpoints
 *
 * Note: `TTypeTestPostBodyStandardName` and `typeTestStandards` must be updated when new standards are added
 */
export const getTypeTestPostBodyFromFormData = (
    formData: TTRDeviceFormData
): TTypeTestPostBody => {
    const typeTestPostBody: TTypeTestPostBody = {} as TTypeTestPostBody

    formData.typeTestDetails.forEach((data) => {
        const fullyTestedName = getFullyTestedPostBodyName(data.name)
        const partiallyTestedName = getPartiallyTestedPostBodyName(data.name)

        typeTestPostBody[fullyTestedName] = data.fullyTested ? "Y" : "N"
        typeTestPostBody[partiallyTestedName] = data.partiallyTested ? "Y" : "N"
    })

    return typeTestPostBody
}

const typeTestDetailsMapping: { [key: string]: string } = {}

typeTestStandards.forEach((standard) => {
    typeTestDetailsMapping[getFullyTestedRegisterName(standard.name)] =
        getFullyTestedLabelName(standard.displayName)
    typeTestDetailsMapping[getPartiallyTestedRegisterName(standard.name)] =
        getPartiallyTestedLabelName(standard.displayName)
})

/**
 * Returns a friendly name for a given type test switch when provided with the react-hook-forms register name.
 * Used for displaying errors
 */
export const getTypeTestLabelNameFromRegisterName = (
    registerName: string
): string => typeTestDetailsMapping[registerName]

export const goToPage = (
    pageIndex: number,
    canEditManufacturer: boolean,
    setCurrentIndex: (index: number) => void
): void => {
    const index = Math.max(canEditManufacturer ? pageIndex : pageIndex - 1, 0)
    setCurrentIndex(index)
}

export const getDisplayName = <T extends string | number | symbol>(
    mappings: Record<T, string>,
    id: T | null
): string => (id ? mappings[id] : "Value not available")
